import { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axiosInstance, { getUserDetails, isFisherOwnerUser, mergeUserDetails,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { getTeamAccessRole, teamAccessRoleFromStaff } from "../../utils/teamRoles";
import TeamRolePicker from "../../components/staff/TeamRolePicker.jsx";

const cardRadius = { borderRadius: "14px" };

const UpdateStaff = () => {
  const history = useHistory();
  const { id } = useParams();
  const [isFisherWideUser, setIsFisherWideUser] = useState(false);
  const fisherID = getUserDetails().FisherID;
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    accessRole: "pond_staff",
    status: false,
  });
  const [ownerVerified, setOwnerVerified] = useState(null);
  const [initialIsOwner, setInitialIsOwner] = useState(false);
  const [targetFisherID, setTargetFisherID] = useState(null);
  const canEditOwnership = Number(targetFisherID) === Number(fisherID);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = getUserDetails();
      let ok = isFisherOwnerUser(u);
      if (!ok && u.id) {
        try {
          const { data } = await axiosInstance.get("api/User/GetStaff", { params: { id: u.id } });
          ok = !!(data && (data.IsFisherOwner === true || data.IsFisherOwner === 1));
          if (ok) mergeUserDetails({ IsFisherOwner: true });
        } catch {
          ok = false;
        }
      }
      if (cancelled) return;
      if (!ok) {
        setOwnerVerified(false);
        setIsPageLoading(false);
        return;
      }
      setOwnerVerified(true);
      setIsPageLoading(true);
      try {
        const response = await axiosInstance.get("api/User/GetStaff", {
          params: { id: id },
        });
        const staff = response.data;

        setFormData({
          fullName: staff.FullName || "",
          email: staff.Email || "",
          accessRole: teamAccessRoleFromStaff(staff),
          status: staff.Status || false,
        });
        setInitialIsOwner(!!staff.IsFisherOwner);
        setTargetFisherID(staff.FisherID);

        setIsFisherWideUser(!!staff.IsFisherOwner);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          const auth = handleLegacySessionExpired(error);
          showErrorToast(auth.message);
        }
      } finally {
        if (!cancelled) setIsPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleChange = (e) => {
    const { id: fieldId, value, type, checked, name } = e.target;
    if (type === "radio") {
      if (name === "accessRole" || name === "teamAccessRole") {
        setFormData((prev) => ({ ...prev, accessRole: value }));
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value === "true",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [fieldId]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRoleChange = (accessRole) => {
    setFormData((prev) => ({ ...prev, accessRole }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    if (!formData.fullName.trim()) {
      showErrorToast("Full name is required");
      setIsSaving(false);
      return;
    }

    const roleDef = getTeamAccessRole(formData.accessRole);

    try {
      if (!canEditOwnership && roleDef.isOwner !== initialIsOwner) {
        showErrorToast("Owner role can only be changed for same-organization staff.");
        setIsSaving(false);
        return;
      }
      await axiosInstance.post(
        `api/User/UpdateStaff`,
        {
          FullName: formData.fullName,
          IsAdmin: roleDef.isAdmin,
          DefaultPondRole: roleDef.defaultPondRole,
          Status: formData.status,
          FisherID: fisherID,
        },
        {
          params: { id: id },
        }
      );
      if (canEditOwnership && roleDef.isOwner !== initialIsOwner) {
        const ownershipRoute = roleDef.isOwner ? "GrantOwnership" : "RevokeOwnership";
        await axiosInstance.post(`api/User/${ownershipRoute}`, {
          FisherID: fisherID,
          StaffID: id,
        });
      }
      showSuccessToast("Staff updated successfully");
      history.push("/staff");
    } catch (error) {
      setIsSaving(false);
      if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        const rawMessage = error.response?.data?.message || "";
        const errorMessage = rawMessage.includes("another fisher account") || rawMessage.includes("another organization")
          ? "Owner role can only be assigned to staff in this organization."
          : (rawMessage || "Unable to update staff member. Please try again.");
        showErrorToast(errorMessage);
      }
    }
  };

  if (ownerVerified === null || isPageLoading) {
    return <Preloader />;
  }
  if (!ownerVerified) {
    return (
      <div className="donezo-page container-fluid px-3 px-md-4 py-5">
        <div
          className="mx-auto bg-white border-0 shadow-sm p-4 p-md-5 text-center"
          style={{ ...cardRadius, maxWidth: 440 }}
        >
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 text-white"
            style={{ width: 56, height: 56, background: "#64748b" }}
          >
            <i className="fa fa-lock fs-4" />
          </div>
          <h2 className="h5 fw-semibold mb-2">Owner access required</h2>
          <p className="text-muted small mb-4">
            Only the pond owner can edit team members. If you need changes, ask your organization owner.
          </p>
          <Link to="/staff" className="btn btn-primary rounded-pill px-4">
            Back to team
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="donezo-page">
      <form onSubmit={handleSubmit} className="container-fluid px-3 px-md-4 py-3 py-md-4">
          <nav aria-label="breadcrumb" className="mb-2">
            <ol className="breadcrumb mb-0 small">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/staff" className="text-decoration-none text-muted">
                  Team
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Edit member
              </li>
            </ol>
          </nav>

          <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <h1 className="h3 fw-semibold text-dark mb-1">Team member</h1>
              <p className="text-muted small mb-0">
                Update name, status, and access type. Email cannot be changed here.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2 ms-lg-auto">
              <Link to="/staff" className="btn btn-outline-secondary rounded-pill px-4">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4 shadow-sm"
                disabled={isSaving}
              >
                <i className="fa fa-save me-2" />
                {isSaving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4" style={cardRadius}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex flex-wrap gap-2">
                <span className="badge rounded-pill px-3 py-2 bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                  General
                </span>
                <Link
                  to={"/changePassword/" + id}
                  className="badge rounded-pill px-3 py-2 bg-light text-secondary text-decoration-none border"
                >
                  <i className="fa fa-key me-1 opacity-75" aria-hidden />
                  Password
                </Link>
                {!isFisherWideUser ? (
                  <Link
                    to={"/ScientistPond/" + id}
                    className="badge rounded-pill px-3 py-2 bg-light text-secondary text-decoration-none border"
                  >
                    <i className="fa fa-water me-1 opacity-75" aria-hidden />
                    Pond access
                  </Link>
                ) : null}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm" style={cardRadius}>
            <div className="card-body p-4 p-lg-5">
              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3" style={{ letterSpacing: "0.04em" }}>
                Profile
              </h2>
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <label htmlFor="fullName" className="form-label small fw-medium text-secondary">
                    Full name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    className="form-control form-control-lg border-0 bg-light"
                    style={{ borderRadius: "10px" }}
                    placeholder="Full name"
                    value={formData.fullName}
                    onChange={handleChange}
                    autoComplete="name"
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="email" className="form-label small fw-medium text-secondary">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="form-control form-control-lg border-0 bg-secondary bg-opacity-10"
                    style={{ borderRadius: "10px" }}
                    value={formData.email}
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                  <div className="form-text small">Login email is fixed for this account.</div>
                </div>
              </div>

              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3 mt-2" style={{ letterSpacing: "0.04em" }}>
                Role
              </h2>
              <p className="small text-muted mb-3">
                Choose the member&apos;s primary role. It applies to all assigned ponds. Use{" "}
                <Link to={`/ScientistPond/${id}`}>Pond access</Link> to assign or unassign ponds.
              </p>
              {!canEditOwnership ? (
                <div className="alert alert-warning py-2 px-3 small mb-3">
                  Owner role can only be changed for staff whose primary organization is this account.
                </div>
              ) : null}
              <TeamRolePicker
                value={formData.accessRole}
                onChange={handleRoleChange}
                disabledRoleValues={!canEditOwnership ? ["owner"] : []}
                idPrefix={`update-staff-role-${id}`}
              />

              <h2 className="h6 text-uppercase text-muted fw-semibold mb-3" style={{ letterSpacing: "0.04em" }}>
                Account status
              </h2>
              <div
                className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-3 rounded-3 bg-light"
                style={{ borderRadius: "10px" }}
              >
                <div>
                  <div className="fw-medium text-dark">Sign-in allowed</div>
                  <div className="small text-muted">Turn off to block login without deleting the account.</div>
                </div>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="status"
                    checked={formData.status}
                    onChange={handleChange}
                    style={{ width: "2.75rem", height: "1.35rem", cursor: "pointer" }}
                  />
                  <label className="form-check-label ms-2 small" htmlFor="status">
                    {formData.status ? "Active" : "Inactive"}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </form>
    </div>
  );
};

export default UpdateStaff;
